/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ErrorHandler, { Category } from '../Error/ErrorHandler';
import ReferencedComponent from '../Utility/Component/ComponentReference/ReferencedComponent';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import IDependency from './IDependency';
import StateComponent from './StateComponent';
import StateEvent from './StateEvent';
import { StateID } from './StateID';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Component for managing [[StateComponent]] flow. */
@cc._decorator.ccclass
export default class StateManager extends ReferencedComponent
{

    /** @hidden */
    @property({ type: cc.Node, visible: true })
    private _initialState: StateComponent = null;
    /** Accessor to first [[StateComponent]] in flow. */
    public get InitialState(): StateComponent { return this._initialState; }
    /** Mutator of first [[StateComponent]] in flow. */
    public set InitialState(state: StateComponent) { this._initialState = state; }

    /** @hidden */
    private _currentState: StateComponent = null;
    /** Current executing state. */
    public get CurrentState(): StateComponent { return this._currentState; }

    /** @hidden */
    private _states: StateComponent[] = [];
    /** Array of all [[StateComponent]]s within this [[StateManager]]. */
    public get States(): StateComponent[] { return this._states; }

    /** Array of handlers for when the [[StateMachine]] completes final [[StateComponent]] */
    @property(
    {
        type: cc.Component.EventHandler,
        tooltip: "i18n:COMPONENT.button.click_events",
        visible: true
    })
    private _onComplete: cc.Component.EventHandler[] = [];

    /** Array of functions for when the [[StateMachine]] completes final [[StateComponent]] */
    private _onCompleteFunctions: (() => any)[] = [];

    /**
     * Array of dependencies that require completion
     * before this state can complete.
     */
    private _dependencies: IDependency[] = [];

    /** True once [[StateManager]] started. */
    private _isStarted: boolean = false;
    public get IsStarted(): boolean { return this._isStarted; }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this._states = this.getComponentsInChildren(StateComponent);

        if (this._initialState && this.InitialState instanceof cc.Node)
        {
            this._initialState = this.InitialState.getComponent(StateComponent);
        }

        this.enabled = false;
    }

    /** @hidden */
    protected update(): void
    {
        this.dependencyHandler();
    }

    /** Event handler to raise start play event. */
    public OnStartPlayEvent(event: any, detail: any): void
    {
        this.StartPlay();
    }

    /** Begings executing first state. */
    public StartPlay = (): StateManager =>
    {
        if (!this._isStarted) { this.start(); }

        if (!this._initialState)
        {
            CLog.warn(`No initial state loaded, will proceed with first in our list.`, LogTypes.Initialization, this);
            this._initialState = this._states[0];
            if (this._initialState.node === this.node)
            {
                this._initialState = this._states[1];
            }
        }

        this._currentState = this._initialState;
        this.clearDependencies(this._currentState);
        this.enabled = true;
        this._isStarted = true;

        return this.dispatchStateEvents();
    }

    /**
     * Add funcitons to be completed when [[StateManager]] reached final state.
     * @param fnOnComplete Function to execute on complete.
     * @returns This [[StateManager]].
     */
    public OnComplete(fnOnComplete: () => any): StateManager
    {
        this._onCompleteFunctions.push(fnOnComplete);
        return this;
    }

    /**
     * Remove function to be completed when [[StateManager]] reached final state.
     * @param fnOnComplete Function to execute on complete.
     * @returns This [[StateManager]].
     */
    public OffComplete(fnOnComplete: () => any): StateManager
    {
        this._onCompleteFunctions = this._onCompleteFunctions.filter(func => func !== fnOnComplete);
        return this;
    }

    /** Checks all dependencies for completion, only moves to next state once all complete. */
    private dependencyHandler(): void
    {
        if (!this._currentState)
        {
            cc.error(`No current state on dependency check.`);
            return;
        }

        // Dependencies at StateMachine level.
        let remainingManagerDependencies: IDependency[]
            = this._dependencies.filter((dependency) => !dependency.IsComplete(this._currentState));

        if (remainingManagerDependencies.length > 0) { return; }

        // Dependencies at State level.
        let remainingStateDependencies: IDependency[]
            = this._currentState.Dependencies.filter((dependency) => !dependency.IsComplete(this._currentState));

        if (remainingStateDependencies.length > 0) { return; }

        this.advanceState();
    }

    /** Advances from current state to the next or optionally next [[StateComponent]]. */
    private advanceState(): StateManager
    {
        if (!this._currentState)
        {
            CLog.error(`Trying to advance to empty next state.`, this, LogTypes.StateManager);
            return this;
        }

        // See if state optional next route is requested and present.
        if (this._currentState.IsOptionalNext)
        {
            if (this._currentState.OptionalNextState)
            {
                return this.setState(this._currentState.OptionalNextState);
            }
            else
            {
                CLog.warn(`State: [${StateID[this._currentState.StateID]}] does not have optional state.`,
                    LogTypes.StateManager, this);
            }
        }

        if (this._currentState.NextState == null)
        {
            this.onCompleteHandler();
            return this;
        }

        // Otherwise to the next state we go.
        return this.setState(this._currentState.NextState);
    }

    /** Handler for when reached a state with no next pointer. */
    private onCompleteHandler = (): void =>
    {
        this.enabled = false;

        ErrorHandler.Instance.DropBreadcrumb(Category.State, "Completed", { node: this.node.name });

        this.triggerOnCompleteHandlers();
        this._onCompleteFunctions.forEach((fnOnComplete) => fnOnComplete());

        this._isStarted = false;
    }

    /** Triggers each onComplete EventHandler. */
    private triggerOnCompleteHandlers(): void
    {
        if (this._onComplete.length < 1) { return; }

        for (let fnOnComplete of this._onComplete)
        {
            if (!fnOnComplete) { return; }

            fnOnComplete.emit(null);
        }
    }

    /**
     * Sets the current pointer to a state.
     * @param toState [[StateComponent]] to become current.
     * @returns This [[StateManager]].
     */
    private setState(toState: StateComponent): StateManager
    {
        this.clearDependencies(toState);

        // Reset so default route is assumed.
        toState.IsOptionalNext = false;
        this._currentState = toState;

        ErrorHandler.Instance.DropBreadcrumb(Category.State, null, {
            node: this.node.name,
            state: toState.name
        });

        return this.dispatchStateEvents();
    }

    /**
     * Resets every dependency to ensure completion is accurate.
     * @param state [[StateComponent]] for which to reset dependencies.default
     * @returns This [[StateManager]].
     */
    private clearDependencies = (state: StateComponent): StateManager =>
    {
        this._dependencies.forEach((dependency) => dependency.Reset());
        state.Dependencies.forEach((dependency) => dependency.Reset());

        return this;
    }

    /**
     * Adds listener for specific states instead of attaching to all game state changes.
     * @param state [[StateComponent]] to add listener too.
     * @param fnOnEvent Function to execute when state becomes active.
     * @param dependency [[IDependency]] to add to provided [[StateComponent]].
     * @returns This [[StateManager]].
     */
    public OnStateChange = (
        state: StateComponent,
        fnOnEvent: (event: StateEvent) => any,
        dependency?: IDependency): StateManager =>
    {
        if (!state)
        {
            CLog.error("State is null.", this, LogTypes.StateManager);
            return this;
        }

        if (dependency)
        {
            state.AddDependency(dependency);
        }

        if (fnOnEvent)
        {
            state.OnStateChange(fnOnEvent);
        }

        return this;
    }

    /**
     * Removes listener for specific states instead of attaching to all game state changes.
     * @param state [[StateComponent]] to remove listener.
     * @param fnOnEvent Function to remove from listening.
     * @returns This [[StateManager]].
     */
    public OffStateChange = (
        state: StateComponent,
        fnOnEvent: (event: StateEvent) => any
    ): StateManager =>
    {
        if (!state || !state.node) { return this; }
        const eventName: string = StateEvent.GetStateEventName(state.StateID);
        state.node.off(eventName, fnOnEvent);
        return this;
    }

    /**
     * Checks if given state is the same as current [[StateComponent]].
     * @param state [[StateComponent]] to compare against current.
     * @returns True if [[StateComponent]] is current.
     */
    public IsCurrentState = (state: StateComponent): boolean =>
    {
        if (!this._currentState) { return false; }

        return this._currentState === state;
    }

    /**
     * Checks if an array of states is the same as current [[StateComponent]].
     * @param stateIDs Array of [[StateID]] to check.
     * @returns True if one [[StateID]] is current.
     */
    public IsCurrentStateContainedIn = (stateIDs: StateComponent[]): boolean =>
    {
        for (let stateID of stateIDs)
        {
            if (this.IsCurrentState(stateID)) { return true; }
        }

        return false;
    }

    public OnAllStateChange(fnOnChange: (event: StateEvent) => void): void
    {
        this.node.on(StateEvent.EventName, fnOnChange);
    }

    public OffAllStateChange(fnOnChange: (event: StateEvent) => void): void
    {
        this.node.off(StateEvent.EventName, fnOnChange);
    }

    /**
     * Sends all events relating to the current state.
     */
    private dispatchStateEvents = (): StateManager =>
    {
        this.node.dispatchEvent(new StateEvent());
        this._currentState.node.dispatchEvent(new StateEvent(this._currentState));
        return this;
    }
}
